import React, { useState } from "react";
import {
  Mail,
  MessageCircle,
  Send,
  CheckCircle,
  AlertCircle,
  Loader,
  Clock,
  Heart,
} from "lucide-react";
import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import { contactApi } from "./contactApi";
import { useTranslation } from "react-i18next";

const ContactUsG2G = () => {
  const { t } = useTranslation();
  const c = (k: string) => t(`contact.${k}`);

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    email?: string;
    message?: string;
  }>({});

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    if (fieldErrors[name as keyof typeof fieldErrors]) {
      setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const errors: { name?: string; email?: string; message?: string } = {};
    if (!form.name.trim()) errors.name = c("errorName");
    if (!form.email.trim()) errors.email = c("errorEmail");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.email = c("errorEmailInvalid");
    }
    if (!form.message.trim()) errors.message = c("errorMessage");
    else if (form.message.trim().length < 10) {
      errors.message = c("errorMessageLength");
    }
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    setError("");
    setFieldErrors({});

    try {
      await contactApi.sendMessage(form);
      setSubmitted(true);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      console.error("Contact form error:", err);
      setError(
        err instanceof Error ? err.message : c("errorGeneral")
      );
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <Mail className="w-5 h-5" />,
      title: c("emailTitle"),
      content: "info@guideline2germany.com",
      link: "mailto:info@guideline2germany.com",
      description: c("emailDesc"),
    },
    {
      icon: (
        <a
          href="https://m.me/guideline2germany"
          target="_blank"
          rel="noopener noreferrer"
          className="w-12 h-12 bg-[#009fe3]/10 rounded-xl flex items-center justify-center text-[#009fe3] group-hover:bg-[#009fe3] group-hover:text-white transition-all duration-200 flex-shrink-0 hover:scale-110 transform"
        >
          <MessageCircle className="w-5 h-5" />
        </a>
      ),
      title: c("chatTitle"),
      content: c("chatContent"),
      link: "https://m.me/guideline2germany",
      description: c("chatDesc"),
    },
    {
      icon: <Clock className="w-5 h-5" />,
      title: c("callTitle"),
      content: c("callContent"),
      link: null,
      description: c("callDesc"),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#009fe3]/3 via-white to-blue-50/30">
      <Breadcrumb title={c("breadcrumb")} />

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="flex items-center justify-center mb-4">
              <div className="w-8 h-8 bg-[#009fe3] rounded-full flex items-center justify-center mr-3">
                <span className="text-white font-bold text-sm">🇧🇩</span>
              </div>
              <span className="text-lg font-semibold text-gray-600">
                {c("badge")}
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 tracking-tight">
              {c("heroTitle")}
              <span className="text-[#009fe3]">{c("heroTitleHighlight")}</span>
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-[#009fe3] via-[#007cb5] to-[#009fe3] rounded-full mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {c("heroSubtitle")}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-stretch">
            {/* Contact Form */}
            <div className="order-2 lg:order-1 flex flex-col">
              {submitted ? (
                <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 text-center flex-1 flex flex-col justify-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {c("successTitle")}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {c("successBody")}
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setForm({ name: "", email: "", message: "" });
                    }}
                    className="text-[#009fe3] hover:text-[#007cb5] font-medium transition-colors"
                  >
                    {c("sendAnother")}
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 space-y-6 flex-1 flex flex-col"
                >
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      {c("formTitle")}
                    </h2>
                    <p className="text-gray-600">
                      {c("formSubtitle")}
                    </p>
                  </div>

                  <div className="flex-1 space-y-6">
                    {/* Name Field */}
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        {c("nameLabel")}
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          className={`w-full px-4 py-4 border-2 rounded-xl transition-all duration-200 outline-none text-gray-900 placeholder-gray-400 ${
                            fieldErrors.name
                              ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                              : "border-gray-200 focus:border-[#009fe3] focus:ring-4 focus:ring-blue-100"
                          }`}
                          placeholder={c("namePlaceholder")}
                          disabled={loading}
                        />
                        {fieldErrors.name && (
                          <div className="flex items-center mt-2 text-red-600 text-sm">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {fieldErrors.name}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Email Field */}
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        {c("emailLabel")}
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          className={`w-full px-4 py-4 border-2 rounded-xl transition-all duration-200 outline-none text-gray-900 placeholder-gray-400 ${
                            fieldErrors.email
                              ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                              : "border-gray-200 focus:border-[#009fe3] focus:ring-4 focus:ring-blue-100"
                          }`}
                          placeholder={c("emailPlaceholder")}
                          disabled={loading}
                        />
                        {fieldErrors.email && (
                          <div className="flex items-center mt-2 text-red-600 text-sm">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {fieldErrors.email}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Message Field */}
                    <div className="space-y-2 flex-1">
                      <label className="block text-sm font-semibold text-gray-700">
                        {c("messageLabel")}
                      </label>
                      <div className="relative flex-1">
                        <textarea
                          name="message"
                          value={form.message}
                          onChange={handleChange}
                          rows={6}
                          className={`w-full px-4 py-4 border-2 rounded-xl transition-all duration-200 outline-none text-gray-900 placeholder-gray-400 resize-none h-full min-h-[150px] ${
                            fieldErrors.message
                              ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                              : "border-gray-200 focus:border-[#009fe3] focus:ring-4 focus:ring-blue-100"
                          }`}
                          placeholder={c("messagePlaceholder")}
                          disabled={loading}
                        />
                        <div className="absolute bottom-3 right-3 text-xs text-gray-400">
                          {form.message.length}/500
                        </div>
                        {fieldErrors.message && (
                          <div className="flex items-center mt-2 text-red-600 text-sm">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {fieldErrors.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Global Error */}
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <div className="flex items-center text-red-800">
                        <AlertCircle className="w-5 h-5 mr-2" />
                        {error}
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-[#009fe3] to-[#007cb5] hover:from-[#007cb5] hover:to-[#006ba3] disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-[1.02] disabled:transform-none"
                  >
                    {loading ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        <span>{c("sendingBtn")}</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>{c("submitBtn")}</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Information Section */}
            <div className="order-1 lg:order-2 flex flex-col space-y-8">
              {/* About Section */}
              <div className="bg-gradient-to-r from-[#009fe3]/10 to-blue-50 rounded-3xl p-8 border border-[#009fe3]/10 flex-1">
                <div className="flex items-center mb-6">
                  <Heart className="w-6 h-6 mr-3 text-[#009fe3]" />
                  <h2 className="text-2xl font-bold text-gray-900">
                    {c("whyTitle")}
                  </h2>
                </div>
                <div className="space-y-4">
                  {[
                    { titleKey: "reason1Title", bodyKey: "reason1Body" },
                    { titleKey: "reason2Title", bodyKey: "reason2Body" },
                    { titleKey: "reason3Title", bodyKey: "reason3Body" },
                  ].map(({ titleKey, bodyKey }) => (
                    <div key={titleKey} className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-[#009fe3]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <CheckCircle className="w-4 h-4 text-[#009fe3]" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1 text-gray-900">
                          {c(titleKey)}
                        </h3>
                        <p className="text-gray-600 text-sm">
                          {c(bodyKey)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact Options */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {c("getInTouch")}
                </h3>
                {contactInfo.map((info, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-2xl p-6 shadow-lg border border-[#009fe3]/10 hover:shadow-xl hover:border-[#009fe3]/20 transition-all duration-200 group"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#009fe3]/10 rounded-xl flex items-center justify-center text-[#009fe3] group-hover:bg-[#009fe3] group-hover:text-white transition-all duration-200 flex-shrink-0">
                        {info.icon}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-1">
                          {info.title}
                        </h4>
                        {info.link ? (
                          <a
                            href={info.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#009fe3] hover:text-[#007cb5] transition-colors font-medium block mb-2"
                          >
                            {info.content}
                          </a>
                        ) : (
                          <p className="text-[#009fe3] font-medium mb-2">
                            {info.content}
                          </p>
                        )}
                        <p className="text-gray-500 text-sm">
                          {info.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="fixed bottom-6 right-6 z-50">
        <a
          href="https://m.me/guideline2germany"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-[#009fe3] hover:bg-[#007cb5] text-white p-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-200 flex items-center group transform hover:scale-110"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="ml-3 font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
            {c("floatBtn")}
          </span>
        </a>
      </div>
    </div>
  );
};

export default ContactUsG2G;
